import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import supabase from '../services/supabaseClient';
import { generateSignedUrl, generateSignedUrlForDownload } from '../services/supabaseClient';
import CitationModal from './CitationModal';
import '../styles/MedicalChronology.css';
import { Play, Pen, Plus, MinusCircle, X, ToggleLeft, ToggleRight, RefreshCw, Settings, MoreVertical, Copy, Trash2 } from 'lucide-react';
import { useAPI } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { Menu, MenuItem, IconButton, Select, Button, Switch, Slider } from '@mui/material';
import { ResizableBox } from 'react-resizable';
import 'react-resizable/css/styles.css';
import FileSidePanel from './FileSidePanel';
import { styled } from '@mui/material/styles';
import { useMedChronService } from '../services/medChronService';

const CustomSwitch = styled(Switch)(({ theme }) => ({
  '& .MuiSwitch-switchBase': {
    '&.Mui-checked': {
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#1976d2',
        opacity: 1,
      },
    },
  },
  '& .MuiSwitch-track': {
    backgroundColor: '#666',
    opacity: 0.5,
  },
}));

const RedSwitch = styled(Switch)(({ theme }) => ({
  '& .MuiSwitch-switchBase': {
    '&.Mui-checked': {
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#f44336',
        opacity: 1,
      },
    },
  },
  '& .MuiSwitch-track': {
    backgroundColor: '#666',
    opacity: 0.5,
  },
}));

const CATEGORY_PRIORITY_ORDER = [
  'subjective',
  'objective',
  'assessment',
  'plan',
  'diagnoses',
  'procedure_details',
  'imaging_type',
  'imaging_target',
  'impression',
  'study_details'
];

const sortVisitDetails = (details, isEditMode) => {
  if (!details) return [];
  return [...details]
    .filter(detail => isEditMode || detail.visit_details?.trim())
    .sort((a, b) => {
      const indexA = CATEGORY_PRIORITY_ORDER.indexOf(a.visit_detail_category);
      const indexB = CATEGORY_PRIORITY_ORDER.indexOf(b.visit_detail_category);
      
      // If both categories are in priority list, sort by priority
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      
      // If only one category is in priority list, it should come first
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      
      // If neither category is in priority list, sort alphabetically
      return a.visit_detail_category.localeCompare(b.visit_detail_category);
    });
};

const sortVisitsByDate = (visitsArray) => {
  return [...visitsArray].sort((a, b) => {
    // Handle null dates by placing them at the end
    if (!a.visit_date && !b.visit_date) return 0;
    if (!a.visit_date) return 1;
    if (!b.visit_date) return -1;
    return new Date(a.visit_date) - new Date(b.visit_date);
  });
};

const MedicalChronology = () => {
  const medChronService = useMedChronService();
  const api = useAPI();
  const { user, sessionStatus, firmContext, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [summaryData, setSummaryData] = useState(null);
  const [visits, setVisits] = useState([]);
  const [viewType, setViewType] = useState('dateOnly');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fileUrl, setFileUrl] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [currentModalFileName, setCurrentModalFileName] = useState('');
  const [fileMapping, setFileMapping] = useState({});
  const [isEditing, setIsEditing] = useState({
    patient_name: false,
    date_of_birth: false,
    date_of_injury: false
  });
  const [editedSummary, setEditedSummary] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [editingVisitDetail, setEditingVisitDetail] = useState(null);
  const [editingVisitHeader, setEditingVisitHeader] = useState(null);
  const [newDetailCategory, setNewDetailCategory] = useState('');
  const [newDetailText, setNewDetailText] = useState('');
  const [showNewDetailForm, setShowNewDetailForm] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [editingHeaderValues, setEditingHeaderValues] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [showDeleteVisitConfirm, setShowDeleteVisitConfirm] = useState(null);
  const [showNewVisitForm, setShowNewVisitForm] = useState(false);
  const [newVisitValues, setNewVisitValues] = useState({
    visit_date: '',
    visit_type_id: '',
    provider_name: '',
    facility_name: '',
    file_id: '',
    page_numbers: [],
    page_numbers_text: ''
  });
  const [formErrors, setFormErrors] = useState([]);
  const [visitTypes, setVisitTypes] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [concision, setConcision] = useState(3);
  const [instructions, setInstructions] = useState([]);
  const [newInstruction, setNewInstruction] = useState('');
  const [processingStatus, setProcessingStatus] = useState(null);
  const [pollInterval, setPollInterval] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showRightPanel, setShowRightPanel] = useState(false);
  const [rightPanelWidth, setRightPanelWidth] = useState(500);
  const [availableFiles, setAvailableFiles] = useState([]);
  const [versions, setVersions] = useState([]);
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [template, setTemplate] = useState(null);
  const [templateId, setTemplateId] = useState(null);
  const [patientBackground, setPatientBackground] = useState(null);
  const [caseName, setCaseName] = useState(null);
  const [physicianName, setPhysicianName] = useState(null);
  const { caseId } = useParams();
  const [collapseTherapyVisits, setCollapseTherapyVisits] = useState(true);
  const [showVersionNameModal, setShowVersionNameModal] = useState(false);
  const [editingVersionName, setEditingVersionName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const parsePageNumbers = (input) => {
    // Split by commas and handle whitespace
    const numbers = input.split(',')
      .map(str => str.trim())
      // Filter out empty strings
      .filter(str => str.length > 0)
      // Convert to integers, handling ranges with '-' or '–'
      .flatMap(str => {
        if (str.includes('-') || str.includes('–')) {
          const [start, end] = str.split(/[-–]/).map(n => parseInt(n.trim()));
          if (!isNaN(start) && !isNaN(end)) {
            // Create array of numbers in the range
            return Array.from({length: end - start + 1}, (_, i) => start + i);
          }
        }
        return parseInt(str);
      })
      // Filter out NaN values
      .filter(num => !isNaN(num));
    
    // Remove duplicates and sort
    return [...new Set(numbers)].sort((a, b) => a - b);
  };

  useEffect(() => {
    if (caseId) {
      fetchCaseDetails(caseId);
      fetchSummaryData(caseId);
      updateFileMapping(caseId);
      fetchVersions(caseId);
      fetchTemplate(caseId);
    }
  }, [caseId]);

  useEffect(() => {
    if (caseId && selectedVersionId) {
      fetchSummaryData(caseId);
      fetchPatientBackground(caseId);
    }
  }, [caseId, selectedVersionId]);

  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) {
        console.error('Error getting current user:', error);
        return;
      }
      setCurrentUser(user);
    };
    
    getCurrentUser();
  }, []);

  useEffect(() => {
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [pollInterval]);

  useEffect(() => {
    const fetchUserSettings = async () => {
      if (!currentUser) return;
      
      const { data, error } = await supabase
        .from('public_users')
        .select('report_concision')
        .eq('user_id', currentUser.id)
        .single();

      if (error) {
        console.error('Error fetching user settings:', error);
        return;
      }

      if (data) {
        setConcision(data.report_concision);
      }
    };

    const fetchInstructions = async () => {
      if (!currentUser) return;

      const { data, error } = await supabase
        .from('med_report_instructions')
        .select('*')
        .eq('user_id', currentUser.id)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching instructions:', error);
        return;
      }

      setInstructions(data || []);
    };

    fetchUserSettings();
    fetchInstructions();
  }, [currentUser]);

  useEffect(() => {
    const fetchFiles = async () => {
      if (editingVisitHeader) {
        const { data, error } = await supabase
          .from('case_files')
          .select('file_id, bucket_url')
          .eq('case_id', caseId);
          
        if (error) {
          console.error('Error fetching files:', error);
          return;
        }
        setAvailableFiles(data);
      }
    };
    
    fetchFiles();
  }, [editingVisitHeader, caseId]);

  useEffect(() => {
    if (sessionStatus === 'expired') {
      navigate('/login');
    } else if (sessionStatus === 'error') {
      toast.error('Having trouble connecting to the server. Will retry shortly...');
    }
  }, [sessionStatus, navigate]);

  useEffect(() => {
    const fetchFiles = async () => {
      if (showNewVisitForm) {
        const { data, error } = await supabase
          .from('case_files')
          .select('file_id, bucket_url')
          .eq('case_id', caseId);
          
        if (error) {
          console.error('Error fetching files:', error);
          return;
        }
        setAvailableFiles(data);
      }
    };
    
    fetchFiles();
  }, [showNewVisitForm, caseId]);

  useEffect(() => {
    if (versions.length > 0) {
      const current = versions.find(v => v.is_current);
      setCurrentVersion(current);
    }
  }, [versions]);

  const fetchSummaryData = async (caseId) => {
    try {
      const chronologyData = await medChronService.getChronology(caseId, selectedVersionId);
      setSummaryData(chronologyData.summary);
      setVisits(chronologyData.visits);
    } catch (error) {
      console.error('Error fetching chronology data:', error);
      toast.error('Error loading chronology data');
    }
  };

  const fetchVersions = async (caseId) => {
    try {
      const versions = await medChronService.getChronologyVersions(caseId);
      setVersions(versions);
      
      // Set the current version as selected
      const currentVersion = versions.find(v => v.is_current);
      if (currentVersion) {
        setSelectedVersionId(currentVersion.version_id);
      }
    } catch (error) {
      console.error('Error fetching versions:', error);
      toast.error('Error loading versions');
    }
  };

  const fetchTemplate = async (caseId) => {
    try {
      const response = await api.get(`/med_chron/template/${caseId}`);
      setTemplate(response.data.template_data);
      setTemplateId(response.data.template_id);
    } catch (error) {
      console.error('Error fetching template:', error);
      toast.error('Error loading template data');
    }
  };

  const groupVisitsByProvider = () => {
    return visits.reduce((acc, visit) => {
      if (!acc[visit.provider_name]) {
        acc[visit.provider_name] = [];
      }
      acc[visit.provider_name].push(visit);
      return acc;
    }, {});
  };

  const groupVisitsByType = () => {
    return visits.reduce((acc, visit) => {
      const typeId = visit.visit_type_id || 'unknown';
      if (!acc[typeId]) {
        acc[typeId] = [];
      }
      acc[typeId].push(visit);
      return acc;
    }, {});
  };

  const formatVisitType = (visitTypeId) => {
    if (!template || !template.visitTypes) {
      console.log('No template or visitTypes:', { template });
      return 'Loading...';
    }
    
    // Get the visit type object directly using the ID as the key
    const visitType = template.visitTypes[visitTypeId];
    
    // Return the name if found, otherwise unknown
    return visitType ? visitType.name : 'Unknown Visit Type';
  };

  const updateFileMapping = async (caseId) => {
    try {
      const { data, error } = await supabase
        .from('case_files')
        .select('file_id, bucket_url')
        .eq('case_id', caseId);

      if (error) {
        console.error('Error fetching file mapping:', error);
        return;
      }

      const newMapping = data.reduce((acc, file) => {
        const fileName = file.bucket_url ? file.bucket_url.split('/').pop() : 'Unknown file';
        acc[file.file_id] = fileName;
        return acc;
      }, {});

      setFileMapping(newMapping);
    } catch (error) {
      console.error('Error updating file mapping:', error);
    }
  };

  const handleCitationClick = async (fileId, pageNumbers) => {
    if (!fileId) {
      console.error('Invalid fileId:', fileId);
      return;
    }

    try {
      const fileName = fileMapping[fileId] || 'Unknown file';
      setCurrentModalFileName(fileName);

      const { data, error } = await supabase
        .from('case_files')
        .select('bucket_url')
        .eq('file_id', fileId)
        .single();

      if (error) throw error;

      const { bucket_url } = data;
      const bucketName = process.env.REACT_APP_SUPABASE_STORAGE_BUCKET;
      const signedUrl = await generateSignedUrl(bucketName, bucket_url);

      if (signedUrl) {
        setFileUrl(signedUrl);
        setPageNumber((pageNumbers && pageNumbers[0]) || 1);
        setShowRightPanel(true);
      }
    } catch (error) {
      console.error('Error handling citation click:', error);
    }
  };

  const shortenFileName = (fileName) => {
    if (fileName.length <= 40) return fileName;
    const start = fileName.slice(0, 20);
    const end = fileName.slice(-17);
    return `${start}...${end}`;
  };

  const logEdit = async (versionId, entityType, entityId, fieldName, oldValue, newValue) => {
    if (!currentUser) {
      console.error('No user found when trying to log edit');
      return;
    }

    try {
      const { error } = await supabase
        .from('med_edit_history')
        .insert({
          version_id: versionId,
          entity_type: entityType,
          entity_id: entityId,
          field_name: fieldName,
          old_value: oldValue?.toString() || '',
          new_value: newValue?.toString() || '',
          edited_by: currentUser.id
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error logging edit:', error);
    }
  };

  const handleVisitDetailUpdate = async (versionId, visitId, oldCategory, newCategory, newDetails) => {
    try {
      const detailData = {
        version_id: selectedVersionId,
        visit_detail_category: newCategory,
        visit_details: newDetails
      };
      await medChronService.updateVisitDetail(caseId, visitId, oldCategory, detailData);

      // Update local state
      setVisits(visits.map(visit => {
        if (visit.visit_id === visitId) {
          return {
            ...visit,
            med_visit_details: visit.med_visit_details.map(detail => 
              detail.visit_detail_category === oldCategory
                ? { 
                    visit_detail_category: newCategory, 
                    visit_details: newDetails 
                  }
                : detail
            )
          };
        }
        return visit;
      }));

      setEditingVisitDetail(null);
    } catch (error) {
      console.error('Error updating visit detail:', error);
      toast.error('Failed to update visit detail');
    }
  };

  const handleDeleteDetail = async (versionId, visitId, category) => {
    setShowDeleteConfirm({ versionId, visitId, category });
  };

  const confirmDelete = async () => {
    const { visitId, category } = showDeleteConfirm;
    
    try {
      await medChronService.deleteVisitDetail(caseId, visitId, category, selectedVersionId);

      // Update local state
      setVisits(visits.map(visit => {
        if (visit.visit_id === visitId) {
          return {
            ...visit,
            med_visit_details: visit.med_visit_details.filter(
              detail => detail.visit_detail_category !== category
            )
          };
        }
        return visit;
      }));
    } catch (error) {
      console.error('Error deleting visit detail:', error);
      toast.error('Failed to delete visit detail');
    } finally {
      setShowDeleteConfirm(null);
    }
  };

  const handleAddDetail = async (versionId, visitId) => {
    setShowNewDetailForm(visitId);
    setNewDetailCategory('');
    setNewDetailText('');
  };

  const handleSubmitNewDetail = async (versionId, visitId) => {
    if (!newDetailCategory || !newDetailText) {
      toast.error('Please fill in both category and details');
      return;
    }

    try {
      const detailData = {
        version_id: selectedVersionId,
        visit_detail_category: newDetailCategory,
        visit_details: newDetailText
      };
      
      await medChronService.addVisitDetail(caseId, visitId, detailData);

      // Update local state
      setVisits(visits.map(visit => {
        if (visit.visit_id === visitId) {
          return {
            ...visit,
            med_visit_details: [
              ...visit.med_visit_details,
              {
                visit_detail_category: newDetailCategory,
                visit_details: newDetailText
              }
            ]
          };
        }
        return visit;
      }));

      // Reset form
      setShowNewDetailForm(null);
      setNewDetailCategory('');
      setNewDetailText('');
    } catch (error) {
      console.error('Error adding visit detail:', error);
      toast.error('Failed to add visit detail');
    }
  };

  const handleDeleteVisit = async (visit) => {
    setShowDeleteVisitConfirm(visit);
  };

  const confirmDeleteVisit = async () => {
    const visit = showDeleteVisitConfirm;
    try {
      await medChronService.deleteVisit(caseId, visit.visit_id, selectedVersionId);

      // Update local state
      setVisits(visits.filter(v => v.visit_id !== visit.visit_id));
      setShowDeleteVisitConfirm(null);
    } catch (error) {
      console.error('Error deleting visit:', error);
      toast.error('Failed to delete visit');
    }
  };

  const handleAddNewVisit = async () => {
    // Validate required fields
    const requiredFields = {
      visit_date: 'Visit date',
      visit_type_id: 'Visit type',
      provider_name: 'Provider name',
      facility_name: 'Facility name',
      file_id: 'File',
      page_numbers: 'Page numbers'
    };

    // Check for empty required fields
    const missingFields = Object.entries(requiredFields)
      .filter(([key]) => {
        if (key === 'page_numbers') {
          return !newVisitValues[key] || newVisitValues[key].length === 0;
        }
        return !newVisitValues[key];
      })
      .map(([_, label]) => label);

    if (missingFields.length > 0) {
      setFormErrors(missingFields);
      toast.error(`${missingFields[0]} is required`);
      return;
    }
    
    try {
      const visitData = {
        ...newVisitValues,
        version_id: selectedVersionId
      };
      const result = await medChronService.createVisit(caseId, visitData);

      // Update local state with the new visit and sort
      const newVisit = {
        ...visitData,
        visit_id: result.visit_id,
        med_visit_details: []
      };

      setVisits(sortVisitsByDate([...visits, newVisit]));
      setShowNewVisitForm(false);
      setNewVisitValues({
        visit_date: '',
        visit_type_id: '',
        provider_name: '',
        facility_name: '',
        file_id: '',
        page_numbers: [],
        page_numbers_text: ''
      });
    } catch (error) {
      console.error('Error adding new visit:', error);
      toast.error('Failed to add new visit');
    }
  };

  const formatDetailCategory = (category, isCollapsed = false) => {
    if (!template || !template.visitTypes) return isCollapsed ? `Sample ${category}` : category;
    
    // Look through all visit types to find the detail category
    for (const visitType of Object.values(template.visitTypes)) {
      if (visitType.details && visitType.details[category]) {
        const name = visitType.details[category].name;
        return isCollapsed ? `Sample ${name}` : name;
      }
    }
    
    // If not found, fallback to formatted category name
    const formatted = category
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    return isCollapsed ? `Sample ${formatted}` : formatted;
  };

  const isTherapyVisit = (visitTypeId) => {
    return visitTypeId === 'pt-visit' || visitTypeId === 'chiro-visit';
  };

  const groupTherapyVisits = (visits) => {
    if (!collapseTherapyVisits) return visits;

    const regularVisits = visits.filter(v => !isTherapyVisit(v.visit_type_id));
    const therapyVisits = visits.filter(v => isTherapyVisit(v.visit_type_id));

    const groupedTherapy = therapyVisits.reduce((acc, visit) => {
      const key = `${visit.visit_type_id}-${visit.provider_name}-${visit.facility_name}`;
      if (!acc[key]) {
        acc[key] = {
          visits: [],
          firstVisit: visit
        };
      }
      acc[key].visits.push(visit);
      return acc;
    }, {});

    const collapsedVisits = Object.values(groupedTherapy).map(group => {
      const visits = group.visits;
      const firstVisit = group.firstVisit;
      
      // Handle null dates when calculating start/end dates
      const validDates = visits
        .map(v => v.visit_date ? new Date(v.visit_date) : null)
        .filter(date => date !== null);
      
      const startDate = validDates.length > 0 ? new Date(Math.min(...validDates)) : null;
      const endDate = validDates.length > 0 ? new Date(Math.max(...validDates)) : null;

      return {
        ...firstVisit,
        isCollapsed: true,
        visitCount: visits.length,
        startDate,
        endDate,
        allVisits: visits
      };
    });

    return [...regularVisits, ...collapsedVisits].sort((a, b) => {
      const aDate = a.startDate || (a.visit_date ? new Date(a.visit_date) : null);
      const bDate = b.startDate || (b.visit_date ? new Date(b.visit_date) : null);
      
      // Handle null dates by placing them at the end
      if (!aDate && !bDate) return 0;
      if (!aDate) return 1;
      if (!bDate) return -1;
      return aDate - bDate;
    });
  };

  const renderVisits = () => {
    let groupedVisits;
    const processedVisits = groupTherapyVisits(visits);

    if (viewType === 'provider') {
      groupedVisits = processedVisits.reduce((acc, visit) => {
        const key = visit.provider_name;
        if (!acc[key]) acc[key] = [];
        acc[key].push(visit);
        return acc;
      }, {});
    } else if (viewType === 'visitType') {
      groupedVisits = processedVisits.reduce((acc, visit) => {
        const key = visit.visit_type_id;
        if (!acc[key]) acc[key] = [];
        acc[key].push(visit);
        return acc;
      }, {});
    } else {
      groupedVisits = { 'All visits': processedVisits };
    }

    return Object.entries(groupedVisits).map(([group, groupVisits]) => (
      <div key={group} className="provider-group">
        <h2>
          {viewType === 'visitType' ? formatVisitType(group) : group} 
          <span className="visit-count">
            {groupVisits.reduce((count, visit) => count + (visit.allVisits?.length || 1), 0)} visit
            {groupVisits.reduce((count, visit) => count + (visit.allVisits?.length || 1), 0) > 1 ? 's' : ''}
          </span>
        </h2>
        {editMode && (
          <button 
            className="add-visit-btn"
            onClick={() => {
              setShowNewVisitForm(true);
              // Pre-fill the provider or visit type based on the current group
              setNewVisitValues({
                ...newVisitValues,
                provider_name: viewType === 'provider' ? group : '',
                visit_type_id: viewType === 'visitType' ? group : ''
              });
            }}
          >
            <Plus size={16} /> New Visit
          </button>
        )}
        {groupVisits.map(visit => (
          <div key={visit.visit_id} className="visit">
            {editMode && (
              <button
                className="delete-visit-btn"
                onClick={() => handleDeleteVisit(visit)}
              >
                <MinusCircle size={16} color="#f44336" />
              </button>
            )}
            {renderVisitHeader(visit)}
            <span className="tooltipText">
              <span className="fileLine">
                <span className="underline">File</span>: {shortenFileName(fileMapping[visit.file_id] || 'Unknown file')}
              </span><br />
              <span className="fileLine">
                <span className="underline">Pages</span>: {visit.page_numbers.join(', ')}
                <span style={{ color: '#ccc', fontStyle: 'italic', marginLeft: '25px' }}>(click to view)</span>
              </span><br />
            </span>
            
            {sortVisitDetails(visit.med_visit_details, editMode).map(detail => (
              <div key={detail.visit_detail_category} className="visit-detail">
                {editMode && (
                  <button
                    className="delete-detail-btn"
                    onClick={() => handleDeleteDetail(visit.version_id, visit.visit_id, detail.visit_detail_category)}
                  >
                    <MinusCircle size={16} color="#f44336" />
                  </button>
                )}
                
                {editingVisitDetail?.visitId === visit.visit_id && 
                 editingVisitDetail?.category === detail.visit_detail_category ? (
                  <div className="new-detail-form">
                    <input
                      type="text"
                      placeholder="Category"
                      value={editingVisitDetail.newCategory}
                      onChange={(e) => setEditingVisitDetail({
                        ...editingVisitDetail,
                        newCategory: e.target.value
                      })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleVisitDetailUpdate(
                            visit.version_id,
                            visit.visit_id,
                            detail.visit_detail_category,
                            editingVisitDetail.newCategory,
                            editingVisitDetail.details
                          );
                        }
                      }}
                    />
                    <textarea
                      placeholder="Details"
                      value={editingVisitDetail.details}
                      onChange={(e) => {
                        setEditingVisitDetail({
                          ...editingVisitDetail,
                          details: e.target.value
                        });
                        autoResizeTextarea(e.target);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleVisitDetailUpdate(
                            visit.version_id,
                            visit.visit_id,
                            detail.visit_detail_category,
                            editingVisitDetail.newCategory,
                            editingVisitDetail.details
                          );
                        } else if (e.key === 'Enter' && e.shiftKey) {
                          // Allow shift+enter to create new line
                          setEditingVisitDetail({
                            ...editingVisitDetail,
                            details: editingVisitDetail.details + '\n'
                          });
                        }
                      }}
                      ref={(textarea) => {
                        if (textarea) {
                          autoResizeTextarea(textarea);
                        }
                      }}
                    />
                    <button onClick={() => handleVisitDetailUpdate(
                      visit.version_id,
                      visit.visit_id,
                      detail.visit_detail_category,
                      editingVisitDetail.newCategory,
                      editingVisitDetail.details
                    )}>
                      Save
                    </button>
                    <button onClick={() => setEditingVisitDetail(null)}>
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div 
                    onClick={() => editMode && setEditingVisitDetail({
                      visitId: visit.visit_id,
                      category: detail.visit_detail_category,
                      newCategory: detail.visit_detail_category,
                      details: detail.visit_details
                    })}
                    className={editMode ? 'editable-detail' : ''}
                  >
                    <strong>{formatDetailCategory(detail.visit_detail_category, visit.isCollapsed)}</strong> {detail.visit_details}
                  </div>
                )}
              </div>
            ))}

            {editMode && showNewDetailForm === visit.visit_id && (
              <div className="new-detail-form">
                <input
                  type="text"
                  placeholder="Category"
                  value={newDetailCategory}
                  onChange={(e) => setNewDetailCategory(e.target.value)}
                />
                <input
                  type="text"
                  placeholder="Details"
                  value={newDetailText}
                  onChange={(e) => setNewDetailText(e.target.value)}
                />
                <button onClick={() => handleSubmitNewDetail(visit.version_id, visit.visit_id)}>
                  Save
                </button>
                <button onClick={() => setShowNewDetailForm(null)}>
                  Cancel
                </button>
              </div>
            )}
            
            {editMode && (
              <button 
                className="add-detail-btn"
                onClick={() => handleAddDetail(visit.version_id, visit.visit_id)}
              >
                <Plus size={16} /> Add Detail
              </button>
            )}
          </div>
        ))}
      </div>
    ));
  };

  const handleRunChronology = async () => {
    if (!caseId) return;

    setIsProcessing(true);
    setIsUpdating(true);
    try {
      const response = await api.post('/med_chron/request', {
        case_id: caseId
      });

      if (response.data.status === 'success') {
        startPolling();
      } else {
        throw new Error('Failed to submit request');
      }
    } catch (error) {
      if (error.message === 'Session expired') {
        return; // API service will handle redirect
      }
      console.error('Error submitting chronology request:', error);
      toast.error('There was an error submitting your request. Please try again.');
      setIsProcessing(false);
      setIsUpdating(false);
    }
  };

  const startPolling = () => {
    if (!caseId) return;

    // Clear any existing interval
    if (pollInterval) {
      clearInterval(pollInterval);
    }

    const checkForVersion = async () => {
      try {
        const { data: currentVersion } = await supabase
          .from('med_chronology_versions')
          .select('version_id, created_at')
          .eq('case_id', caseId)
          .eq('is_current', true)
          .single();

        if (currentVersion?.version_id) {
          // Check if this is a new version
          if (currentVersion.created_at > new Date(Date.now() - 30000).toISOString()) { // within last 30 seconds
            clearInterval(pollInterval);
            setPollInterval(null);
            setIsProcessing(false);
            setIsUpdating(false);
            fetchSummaryData(caseId);
          }
        }
      } catch (error) {
        // Ignore errors - just keep polling
      }
    };

    // Poll every 5 seconds for up to 30 minutes
    const interval = setInterval(checkForVersion, 5000);
    setPollInterval(interval);

    // Initial check
    checkForVersion();
  };

  const handleProcessingComplete = () => {
    setIsProcessing(false);
    setIsUpdating(false);
    fetchSummaryData(caseId);
  };

  const renderProgressBar = () => {
    if (!processingStatus) return null;

    let progress = 0;
    let statusText = '';

    switch (processingStatus.stage) {
      case 'document_processing':
        if (processingStatus.document_number === -1) {
          progress = 0;
          statusText = 'Initializing...';
        } else {
          progress = (processingStatus.document_number / processingStatus.total_document_count) * 90;
          statusText = `Processing document ${processingStatus.document_number}/${processingStatus.total_document_count}`;
        }
        break;
      case 'finalizing':
        progress = 95;
        statusText = 'Finalizing chronology...';
        break;
      case 'finished':
        progress = 100;
        statusText = 'Complete!';
        break;
    }

    return (
      <div className="progress-container">
        <div className="progress-bar">
          <div 
            className="progress-fill"
            style={{ 
              width: `${progress}%`,
              transition: 'width 0.5s ease-in-out'
            }}
          />
        </div>
        <div className="progress-text">{statusText}</div>
      </div>
    );
  };

  const handleEditClick = (field) => {
    setIsEditing(prev => ({ ...prev, [field]: true }));
    setEditedSummary(prev => ({ ...prev, [field]: summaryData[field] }));
  };

  const handleSaveEdit = async (field) => {
    try {
      await medChronService.updateSummary(caseId, field, editedSummary[field]);

      // Update the UI
      setSummaryData(prev => ({ ...prev, [field]: editedSummary[field] }));
      setIsEditing(prev => ({ ...prev, [field]: false }));
    } catch (error) {
      console.error('Error saving edit:', error);
      toast.error('Failed to save changes');
    }
  };

  const handleCancelEdit = (field) => {
    setIsEditing(prev => ({ ...prev, [field]: false }));
    setEditedSummary(prev => ({ ...prev, [field]: summaryData[field] }));
  };

  const renderSummaryField = (field, label, type = 'text') => {
    if (isEditing[field]) {
      return (
        <div className="summary-field">
          <strong>{label}:</strong>{' '}
          <input
            type={type}
            value={type === 'date' 
              ? (editedSummary[field]?.split('T')[0] || '') 
              : (editedSummary[field] || '')}
            onChange={(e) => setEditedSummary(prev => ({ ...prev, [field]: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSaveEdit(field);
              if (e.key === 'Escape') handleCancelEdit(field);
            }}
            className="summary-edit-input"
          />
          <button onClick={() => handleSaveEdit(field)} className="summary-edit-btn">✓</button>
          <button onClick={() => handleCancelEdit(field)} className="summary-edit-btn">✕</button>
        </div>
      );
    }

    return (
      <div className="summary-field" onClick={() => handleEditClick(field)}>
        <strong>{label}:</strong>{' '}
        <span className="editable-text">
          {type === 'date' && summaryData[field]
            ? new Date(summaryData[field] + 'T00:00:00').toLocaleDateString()
            : (summaryData[field] || '')}
        </span>
      </div>
    );
  };

  const handleVisitHeaderUpdate = async (visit, newDate, newTypeId, newProvider, newFileId, newPageNumbers, newFacility) => {
    try {
      const updateData = {
        version_id: selectedVersionId,
        visit_date: newDate,
        visit_type_id: newTypeId,
        provider_name: newProvider,
        facility_name: newFacility,
        file_id: newFileId,
        page_numbers: newPageNumbers
      };

      await medChronService.updateVisit(caseId, visit.visit_id, updateData);

      // Update local state and sort
      const updatedVisits = visits.map(v => 
        v.visit_id === visit.visit_id
          ? { ...v, ...updateData }
          : v
      );
      setVisits(sortVisitsByDate(updatedVisits));

      setEditingVisitHeader(null);
      setEditingHeaderValues(null);
    } catch (error) {
      console.error('Error updating visit header:', error);
      toast.error('Failed to update visit header');
    }
  };

  const handleEditHeaderClick = (visit) => {
    setEditingVisitHeader(visit.visit_id);
    setEditingHeaderValues({
      visit_date: visit.visit_date ? new Date(visit.visit_date).toISOString().split('T')[0] : '',
      visit_type_id: visit.visit_type_id,
      provider_name: visit.provider_name,
      facility_name: visit.facility_name,
      file_id: visit.file_id,
      page_numbers: visit.page_numbers || []
    });
  };

  const renderVisitHeaderForm = (visit, editingHeaderValues) => {
    return (
      <div className="new-detail-form visit-header-form">
        <input
          type="date"
          value={editingHeaderValues.visit_date}
          onChange={(e) => setEditingHeaderValues({
            ...editingHeaderValues,
            visit_date: e.target.value
          })}
        />
        <select
          value={editingHeaderValues.visit_type_id || ''}
          onChange={(e) => setEditingHeaderValues({
            ...editingHeaderValues,
            visit_type_id: e.target.value
          })}
        >
          <option value="">Select visit type</option>
          {template?.visitTypes && Object.entries(template.visitTypes).map(([typeId, typeData]) => (
            <option key={typeId} value={typeId}>
              {typeData.name}
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Provider"
          value={editingHeaderValues.provider_name}
          onChange={(e) => setEditingHeaderValues({
            ...editingHeaderValues,
            provider_name: e.target.value
          })}
        />
        <input
          type="text"
          placeholder="Facility"
          value={editingHeaderValues.facility_name || ''}
          onChange={(e) => setEditingHeaderValues({
            ...editingHeaderValues,
            facility_name: e.target.value
          })}
        />
        <select
          value={editingHeaderValues.file_id || ''}
          onChange={(e) => setEditingHeaderValues({
            ...editingHeaderValues,
            file_id: e.target.value
          })}
          className="file-selector"
        >
          <option value="">Select file</option>
          {availableFiles.map(file => (
            <option key={file.file_id} value={file.file_id}>
              {shortenFileName(file.bucket_url.split('/').pop())}
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Page numbers (e.g., 1,2,3)"
          value={editingHeaderValues.page_numbers?.join(',') || ''}
          onChange={(e) => setEditingHeaderValues({
            ...editingHeaderValues,
            page_numbers: e.target.value.split(',').map(n => parseInt(n.trim())).filter(n => !isNaN(n))
          })}
          className="page-numbers-input"
        />
        <button onClick={() => handleVisitHeaderUpdate(
          visit,
          editingHeaderValues.visit_date,
          editingHeaderValues.visit_type_id,
          editingHeaderValues.provider_name,
          editingHeaderValues.file_id,
          editingHeaderValues.page_numbers || [],
          editingHeaderValues.facility_name
        )}>
          Save
        </button>
        <button onClick={() => {
          setEditingVisitHeader(null);
          setEditingHeaderValues(null);
        }}>
          Cancel
        </button>
      </div>
    );
  };

  const renderNewVisitForm = () => (
    <div className="modal-overlay">
      <div className="confirmation-modal">
        <button className="close-modal" onClick={() => {
          setShowNewVisitForm(false);
          setFormErrors([]);
        }}>
          <X size={20} />
        </button>
        <h3>Add New Visit</h3>
        <div className="new-visit-form">
          <input
            type="date"
            value={newVisitValues.visit_date}
            onChange={(e) => {
              setNewVisitValues({
                ...newVisitValues,
                visit_date: e.target.value
              });
              setFormErrors(prev => prev.filter(f => f !== 'Visit date'));
            }}
            className={formErrors.includes('Visit date') ? 'error' : ''}
          />
          <select
            value={newVisitValues.visit_type_id || ''}
            onChange={(e) => {
              setNewVisitValues({
                ...newVisitValues,
                visit_type_id: e.target.value
              });
              setFormErrors(prev => prev.filter(f => f !== 'Visit type'));
            }}
            className={formErrors.includes('Visit type') ? 'error' : ''}
          >
            <option value="">Select visit type</option>
            {template?.visitTypes && Object.entries(template.visitTypes).map(([typeId, typeData]) => (
              <option key={typeId} value={typeId}>
                {typeData.name}
              </option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Provider"
            value={newVisitValues.provider_name}
            onChange={(e) => {
              setNewVisitValues({
                ...newVisitValues,
                provider_name: e.target.value
              });
              setFormErrors(prev => prev.filter(f => f !== 'Provider name'));
            }}
            className={formErrors.includes('Provider name') ? 'error' : ''}
          />
          <input
            type="text"
            placeholder="Facility"
            value={newVisitValues.facility_name}
            onChange={(e) => {
              setNewVisitValues({
                ...newVisitValues,
                facility_name: e.target.value
              });
              setFormErrors(prev => prev.filter(f => f !== 'Facility name'));
            }}
            className={formErrors.includes('Facility name') ? 'error' : ''}
          />
          <select
            value={newVisitValues.file_id || ''}
            onChange={(e) => {
              setNewVisitValues({
                ...newVisitValues,
                file_id: e.target.value
              });
              setFormErrors(prev => prev.filter(f => f !== 'File'));
            }}
            className={`file-selector ${formErrors.includes('File') ? 'error' : ''}`}
          >
            <option value="">Select file</option>
            {availableFiles.map(file => (
              <option key={file.file_id} value={file.file_id}>
                {shortenFileName(file.bucket_url.split('/').pop())}
              </option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Page numbers (e.g., 1, 2-5, 8)"
            value={newVisitValues.page_numbers_text || ''}
            onChange={(e) => {
              const inputText = e.target.value;
              setNewVisitValues({
                ...newVisitValues,
                page_numbers_text: inputText,
                page_numbers: parsePageNumbers(inputText)
              });
              setFormErrors(prev => prev.filter(f => f !== 'Page numbers'));
            }}
            className={`page-numbers-input ${formErrors.includes('Page numbers') ? 'error' : ''}`}
          />
        </div>
        <div className="modal-buttons">
          <button onClick={handleAddNewVisit} className="save-btn">
            Add Visit
          </button>
          <button onClick={() => {
            setShowNewVisitForm(false);
            setFormErrors([]);
          }} className="cancel-btn">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  const renderVisitHeader = (visit) => {
    if (editingVisitHeader === visit.visit_id) {
      return renderVisitHeaderForm(visit, editingHeaderValues);
    }

    if (visit.isCollapsed) {
      return (
        <h3 className="citationTooltip">
          <span 
            className="highlightedText" 
            onClick={() => handleCitationClick(visit.file_id, visit.page_numbers)}
          >
            {visit.visitCount} {formatVisitType(visit.visit_type_id)}s, {' '}
            {visit.startDate ? new Date(visit.startDate).toLocaleDateString() : 'Unknown'} - {' '}
            {visit.endDate ? new Date(visit.endDate).toLocaleDateString() : 'Unknown'}, {' '}
            {visit.provider_name}
            {visit.facility_name && <span> ({visit.facility_name})</span>}
          </span>
          {editMode && (
            <button 
              className="edit-header-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleEditHeaderClick(visit);
              }}
            >
              <Pen size={14} />
            </button>
          )}
          <span className="tooltipText">
            <span className="fileLine">
              <span className="underline">File</span>: {shortenFileName(fileMapping[visit.file_id] || 'Unknown file')}
            </span><br />
            <span className="fileLine">
              <span className="underline">Pages</span>: {visit.page_numbers?.join(', ') || 'N/A'}
              <span style={{ color: '#ccc', fontStyle: 'italic', marginLeft: '25px' }}>(click to view)</span>
            </span><br />
          </span>
        </h3>
      );
    }

    return (
      <h3 className="citationTooltip">
        {editingVisitHeader === visit.visit_id ? (
          renderVisitHeaderForm(visit, editingHeaderValues)
        ) : (
          <>
            <span className="highlightedText" onClick={() => handleCitationClick(visit.file_id, visit.page_numbers)}>
              {visit.visit_date ? new Date(visit.visit_date + 'T00:00:00').toLocaleDateString() : 'Unknown date'}, {formatVisitType(visit.visit_type_id)}, {visit.provider_name}
              {visit.facility_name && <span> ({visit.facility_name})</span>}
            </span>
            {editMode && (
              <button 
                className="edit-header-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditHeaderClick(visit);
                }}
              >
                <Pen size={14} />
              </button>
            )}
            <span className="tooltipText">
              <span className="fileLine">
                <span className="underline">File</span>: {shortenFileName(fileMapping[visit.file_id] || 'Unknown file')}
              </span><br />
              <span className="fileLine">
                <span className="underline">Pages</span>: {(visit.page_numbers && visit.page_numbers.length > 0) ? visit.page_numbers.join(', ') : 'N/A'}
                <span style={{ color: '#ccc', fontStyle: 'italic', marginLeft: '25px' }}>(click to view)</span>
              </span><br />
            </span>
          </>
        )}
      </h3>
    );
  };

  const handleExport = async () => {
    if (!caseId) return;

    setIsExporting(true);
    try {
      const response = await api.post('/med_chron/export', { case_id: caseId });
      
      if (!response.data?.data?.path) {
        throw new Error('No export path received from server');
      }

      const bucketName = response.data.data.bucket;
      if (!bucketName) {
        throw new Error('Export bucket not configured');
      }

      const signedUrl = await generateSignedUrlForDownload(bucketName, response.data.data.path);
      if (!signedUrl) {
        throw new Error('Failed to generate signed URL');
      }

      // Fetch the PDF data as a blob
      const pdfResponse = await fetch(signedUrl);
      const pdfBlob = await pdfResponse.blob();
      
      // Create a blob URL and trigger download
      const blobUrl = window.URL.createObjectURL(new Blob([pdfBlob], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = response.data.data.path.split('/').pop() || 'medical_chronology.pdf';
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

    } catch (error) {
      if (error.message === 'Session expired') {
        return; // API service will handle redirect
      }
      console.error('Error exporting chronology:', error);
      toast.error('Failed to export chronology');
    } finally {
      setIsExporting(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!currentUser) return;

    try {
      // Update concision setting
      const { error: settingsError } = await supabase
        .from('public_users')
        .upsert({
          user_id: currentUser.id,
          report_concision: concision
        });

      if (settingsError) throw settingsError;

      toast.success('Settings saved successfully');
      setShowSettingsModal(false);
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    }
  };

  const handleAddInstruction = async () => {
    if (!newInstruction.trim() || !currentUser) return;

    try {
      const { data, error } = await supabase
        .from('med_report_instructions')
        .insert({
          user_id: currentUser.id,
          instruction: newInstruction.trim()
        })
        .select()
        .single();

      if (error) throw error;

      setInstructions([...instructions, data]);
      setNewInstruction('');
    } catch (error) {
      console.error('Error adding instruction:', error);
      toast.error('Failed to add instruction');
    }
  };

  const handleDeleteInstruction = async (instructionId) => {
    try {
      const { error } = await supabase
        .from('med_report_instructions')
        .delete()
        .eq('instruction_id', instructionId);

      if (error) throw error;

      setInstructions(instructions.filter(i => i.instruction_id !== instructionId));
    } catch (error) {
      console.error('Error deleting instruction:', error);
      toast.error('Failed to delete instruction');
    }
  };

  const renderSettingsModal = () => (
    <div className="modal-overlay">
      <div className="settings-modal">
        <button className="close-modal" onClick={() => setShowSettingsModal(false)}>
          <X size={20} />
        </button>
        <h3>Customize Your Reports</h3>
        
        <div className="concision-slider">
          <h4>Report Detail Level</h4>
          <div className="slider-labels">
            <span>Concise</span>
            <span>Verbose</span>
          </div>
          <Slider
            value={concision}
            onChange={(_, value) => setConcision(value)}
            min={1}
            max={5}
            step={1}
            marks
            className="custom-slider"
          />
        </div>

        <div className="instructions-section">
          <h4>Specific Instructions</h4>
          {instructions.map((instruction) => (
            <div key={instruction.instruction_id} className="instruction-item">
              <p>{instruction.instruction}</p>
              <button 
                className="delete-instruction"
                onClick={() => handleDeleteInstruction(instruction.instruction_id)}
              >
                <X size={16} />
              </button>
            </div>
          ))}
          
          <div className="add-instruction">
            <textarea
              value={newInstruction}
              onChange={(e) => setNewInstruction(e.target.value)}
              placeholder="Enter new instruction..."
            />
            <button onClick={handleAddInstruction}>Add Instruction</button>
          </div>
        </div>

        <div className="modal-buttons">
          <button onClick={handleSaveSettings} className="save-btn">
            Save Settings
          </button>
          <button onClick={() => setShowSettingsModal(false)} className="cancel-btn">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  const autoResizeTextarea = (element) => {
    element.style.height = 'auto';
    element.style.height = (element.scrollHeight) + 'px';
  };

  const handleResize = (width) => {
    setRightPanelWidth(width);
    document.documentElement.style.setProperty('--panel-width', `${width}px`);
  };

  // Set initial panel width
  useEffect(() => {
    if (showRightPanel) {
      document.documentElement.style.setProperty('--panel-width', `${rightPanelWidth}px`);
    }
  }, [showRightPanel, rightPanelWidth]);

  const renderRightPanel = () => {
    return (
      <FileSidePanel
        isOpen={showRightPanel}
        onClose={() => setShowRightPanel(false)}
        fileName={currentModalFileName}
        fileUrl={fileUrl}
        initialPage={pageNumber - 1}
        initialWidth={rightPanelWidth}
        minWidth={300}
        maxWidth={800}
        onWidthChange={handleResize}
      />
    );
  };

  const handleVersionChange = (e) => {
    setSelectedVersionId(e.target.value);
  };

  const fetchPatientBackground = async (caseId) => {
    try {
      const response = await api.get(`/chronology/${caseId}/patient-background`, {
        params: { version_id: selectedVersionId }
      });
      setPatientBackground(response.data);
    } catch (error) {
      console.error('Error fetching patient background:', error);
      toast.error('Error loading patient background data');
    }
  };

  const fetchCaseDetails = async (caseId) => {
    try {
      const response = await api.get(`/cases/${caseId}/details`);
      setCaseName(response.data.case_name);
      const physicianName = response.data.physician_name;
      setPhysicianName(physicianName);
    } catch (error) {
      console.error('Error fetching case details:', error);
    }
  };

  const handleVersionNameUpdate = async () => {
    try {
      await medChronService.updateVersionName(selectedVersionId, editingVersionName);
      // Refresh versions list
      await fetchVersions(caseId);
      setShowVersionNameModal(false);
      toast.success('Version name updated successfully');
    } catch (error) {
      console.error('Error updating version name:', error);
      toast.error('Error updating version name');
    }
  };

  const handleSetVersionAsCurrent = async () => {
    try {
      await medChronService.setVersionAsCurrent(selectedVersionId);
      // Refresh versions list
      await fetchVersions(caseId);
      toast.success('Version set as current successfully');
    } catch (error) {
      console.error('Error setting version as current:', error);
      toast.error('Error setting version as current');
    }
  };

  const handleDuplicateVersion = async (versionId) => {
    try {
      setIsLoading(true);
      const response = await api.post(`/chronology/versions/${versionId}/duplicate`);
      
      // Refresh versions list
      await fetchVersions(caseId);
      toast.success('Version duplicated successfully');
    } catch (error) {
      console.error('Error duplicating version:', error);
      toast.error('Error duplicating version');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteVersion = async (versionId) => {
    try {
      setIsLoading(true);
      const response = await api.delete(`/chronology/versions/${versionId}`);
      
      // If we're deleting the currently selected version, select another one
      if (selectedVersionId === versionId) {
        const otherVersion = versions.find(v => v.version_id !== versionId);
        if (otherVersion) {
          setSelectedVersionId(otherVersion.version_id);
        } else {
          setSelectedVersionId(null);
        }
      }
      
      // Refresh versions list
      await fetchVersions(caseId);
      toast.success('Version deleted successfully');
    } catch (error) {
      console.error('Error deleting version:', error);
      toast.error(error.response?.data?.detail || 'Error deleting version');
    } finally {
      setIsLoading(false);
    }
  };

  const renderVersionSelector = () => (
    <div className="version-selector-container">
      <div className="version-controls-row" style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
        <Select
          value={selectedVersionId || ''}
          onChange={(e) => handleVersionChange(e)}
          style={{ minWidth: '300px' }}
          disabled={isLoading}
        >
          {versions.map((version) => (
            <MenuItem key={version.version_id} value={version.version_id}>
              {version.version_name} {version.is_current ? '(Current)' : ''}
            </MenuItem>
          ))}
        </Select>
        {selectedVersionId && (
          <>
            <IconButton
              size="small"
              onClick={(event) => setAnchorEl(event.currentTarget)}
              disabled={isLoading}
            >
              <MoreVertical size={16} />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
            >
              <MenuItem 
                onClick={() => {
                  const currentVersion = versions.find(v => v.version_id === selectedVersionId);
                  setEditingVersionName(currentVersion?.version_name || '');
                  setShowVersionNameModal(true);
                  setAnchorEl(null);
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Pen size={16} />
                  <span>Edit Name</span>
                </div>
              </MenuItem>
              <MenuItem 
                onClick={() => {
                  handleSetVersionAsCurrent();
                  setAnchorEl(null);
                }}
                disabled={currentVersion?.version_id === selectedVersionId}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <ToggleRight size={16} />
                  <span>Set as Current</span>
                </div>
              </MenuItem>
              <MenuItem 
                onClick={() => {
                  handleDuplicateVersion(selectedVersionId);
                  setAnchorEl(null);
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Copy size={16} />
                  <span>Duplicate</span>
                </div>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  if (window.confirm('Are you sure you want to delete this version? This action cannot be undone.')) {
                    handleDeleteVersion(selectedVersionId);
                  }
                  setAnchorEl(null);
                }}
                disabled={currentVersion?.version_id === selectedVersionId}
                style={{ color: currentVersion?.version_id === selectedVersionId ? 'inherit' : '#f44336' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Trash2 size={16} />
                  <span>Delete</span>
                </div>
              </MenuItem>
            </Menu>
          </>
        )}
      </div>
      {showVersionNameModal && (
        <div className="modal-overlay">
          <div className="confirmation-modal">
            <button className="close-modal" onClick={() => setShowVersionNameModal(false)}>
              <X size={20} />
            </button>
            <h3>Edit Version Name</h3>
            <input
              type="text"
              value={editingVersionName}
              onChange={(e) => setEditingVersionName(e.target.value)}
              placeholder="Enter version name"
              className="version-name-input"
            />
            <div className="modal-buttons">
              <button 
                onClick={handleVersionNameUpdate} 
                className="save-btn"
                disabled={!editingVersionName.trim()}
              >
                Save
              </button>
              <button onClick={() => setShowVersionNameModal(false)} className="cancel-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const handleMarkReviewed = async () => {
    try {
      const response = await api.post(`/chronology/${caseId}/mark-reviewed`);
      if (response.data.status === 'success') {
        toast.success(firmContext?.firmSettings?.enable_qa ? 
          'Case submitted to QA' : 
          'Case marked as finished'
        );
        // Optionally navigate away or refresh the page
        navigate('/cases');
      }
    } catch (error) {
      console.error('Error marking case as reviewed:', error);
      toast.error('Failed to update case status');
    }
  };

  const renderHeader = () => (
    <div className="header">
      <div className="header-top">
        <div className="chronology-header">
          <h1>{caseName || 'Medical Chronology'}</h1>
          {physicianName && <div className="physician-name">Physician: {physicianName}</div>}
        </div>
        <div className="header-buttons">
          {hasPermission('case_actions', 'mark_reviewed') && 
           firmContext?.roleName === 'analyst' && (
            <button
              onClick={handleMarkReviewed}
              className="px-4 py-2 font-semibold text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors duration-200 shadow-sm"
            >
              {firmContext?.firmSettings?.enable_qa ? "Submit to QA" : "Mark as Finished"}
            </button>
          )}
          <button 
            className="customize-reports-btn"
            onClick={() => templateId && navigate(`/templates/${templateId}`)}
            disabled={!templateId}
            style={{ display: firmContext?.roleName === 'analyst' ? 'none' : 'flex' }}
          >
            <Settings size={16} />
            <div className="btn-text">
              <span>Customize reports</span>
              {physicianName && <span>for {physicianName}</span>}
            </div>
          </button>
          {visits.length > 0 && (
            <button 
              className={`export-btn ${isExporting ? 'exporting' : ''}`}
              onClick={handleExport}
              disabled={isExporting}
            >
              {isExporting ? (
                <RefreshCw className="spin" size={16} />
              ) : (
                'Generate PDF'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="medical-chronology-container">
      <div className={`medical-chronology ${showRightPanel ? 'with-right-panel' : ''}`}>
        {renderHeader()}
        {summaryData && (
          <div className="summary-data">
            <div className="summary-fields">
              {summaryData.date_of_birth && renderSummaryField('date_of_birth', 'DOB', 'date')}
              {summaryData.date_of_injury && renderSummaryField('date_of_injury', 'DOI', 'date')}
            </div>
            {summaryData.accident_description && (
              <div className="accident-details">
                <strong>Accident Details:</strong> {summaryData.accident_description}
              </div>
            )}
            {patientBackground && (
              <div className="patient-background">
                {patientBackground.prior_medical_history && patientBackground.prior_medical_history !== 'Unknown' && (
                  <div className="background-field">
                    <strong>Prior Medical History:</strong> {patientBackground.prior_medical_history}
                  </div>
                )}
                {patientBackground.pre_existing_conditions && patientBackground.pre_existing_conditions !== 'Unknown' && (
                  <div className="background-field">
                    <strong>Pre-existing Conditions:</strong> {patientBackground.pre_existing_conditions}
                  </div>
                )}
                {patientBackground.family_history && patientBackground.family_history !== 'Unknown' && (
                  <div className="background-field">
                    <strong>Family History:</strong> {patientBackground.family_history}
                  </div>
                )}
                {patientBackground.social_history && patientBackground.social_history !== 'Unknown' && (
                  <div className="background-field">
                    <strong>Social History:</strong> {patientBackground.social_history}
                  </div>
                )}
                {patientBackground.allergies_medications && patientBackground.allergies_medications !== 'Unknown' && (
                  <div className="background-field">
                    <strong>Allergies & Medications:</strong> {patientBackground.allergies_medications}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <div className="view-controls">
          <div className="view-controls-left">
            <div className="view-selector">
              <label>Organized by</label>
              <select value={viewType} onChange={(e) => setViewType(e.target.value)}>
                <option value="dateOnly">Date only</option>
                <option value="provider">Provider</option>
                <option value="visitType">Visit Type</option>
              </select>
            </div>
            <div className="therapy-toggle">
              <CustomSwitch
                checked={collapseTherapyVisits}
                onChange={(e) => {
                  setCollapseTherapyVisits(e.target.checked);
                  if (e.target.checked && editMode) {
                    setEditMode(false);
                  }
                }}
              />
              <span>Collapse Therapy Visits</span>
            </div>
            <div className="edit-mode-toggle">
              <RedSwitch
                checked={editMode}
                onChange={(e) => {
                  setEditMode(e.target.checked);
                  if (e.target.checked && collapseTherapyVisits) {
                    setCollapseTherapyVisits(false);
                  }
                }}
              />
              <span>Edit Mode</span>
            </div>
          </div>
          <div className="version-controls">
            {renderVersionSelector()}
          </div>
        </div>
        <div className="visits-container">
          {renderVisits()}
        </div>
        <CitationModal
          isOpen={isModalOpen}
          onRequestClose={() => setIsModalOpen(false)}
          fileUrl={fileUrl}
          pageNumber={pageNumber}
          filename={currentModalFileName}
        />
        {showDeleteConfirm && (
          <div className="modal-overlay">
            <div className="confirmation-modal">
              <button className="close-modal" onClick={() => setShowDeleteConfirm(null)}>
                <X size={20} />
              </button>
              <h3>Confirm Delete</h3>
              <p>Are you sure you want to delete this detail?</p>
              <div className="modal-buttons">
                <button onClick={confirmDelete} className="delete-btn">
                  Delete
                </button>
                <button onClick={() => setShowDeleteConfirm(null)} className="cancel-btn">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        {showDeleteVisitConfirm && (
          <div className="modal-overlay">
            <div className="confirmation-modal">
              <button className="close-modal" onClick={() => setShowDeleteVisitConfirm(null)}>
                <X size={20} />
              </button>
              <h3>Confirm Delete Visit</h3>
              <p>Are you sure you want to delete this visit?</p>
              <div className="modal-buttons">
                <button onClick={confirmDeleteVisit} className="delete-btn">
                  Delete
                </button>
                <button onClick={() => setShowDeleteVisitConfirm(null)} className="cancel-btn">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        {showNewVisitForm && renderNewVisitForm()}
        {showSettingsModal && renderSettingsModal()}
      </div>
      {showRightPanel && (
        <div className="file-side-panel-container">
          <FileSidePanel
            isOpen={showRightPanel}
            onClose={() => setShowRightPanel(false)}
            fileName={currentModalFileName}
            fileUrl={fileUrl}
            initialPage={pageNumber - 1}
            initialWidth={rightPanelWidth}
            minWidth={300}
            maxWidth={800}
            onWidthChange={handleResize}
          />
        </div>
      )}
    </div>
  );
};

export default MedicalChronology;
